import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Building2, Users, FileText, ArrowRight, CheckCircle2, BarChart3, Activity, TrendingUp, AlertTriangle } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsCommercial({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-white text-[#002366] font-sans" data-id="element-1921">
      <SimonsHeader activePage="commercial" setActivePage={setActivePage} data-id="element-1922" />

      {/* Hero Section */}
      <section className="bg-[#002366] text-white py-24 px-8 relative overflow-hidden" data-id="element-1923">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10" data-id="element-1924">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-1925">
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight tracking-tighter" data-id="element-1926">
              Enterprise Risk <br data-id="element-1927" />
              <span className="text-[#FFD700]" data-id="element-1928">Assessment.</span>
            </h1>
            <p className="text-xl text-white/60 mb-10 max-w-xl font-medium" data-id="element-1929">
              Strategic B2B protection designed for operational resilience. We
              analyze your exposure to provide institutional-grade coverage.
            </p>
            <div className="flex gap-4" data-id="element-1930">
              <button onClick={() => setActivePage('quote')} className="px-8 py-4 bg-[#D32F2F] text-white rounded-xl font-bold hover:bg-[#B71C1C] transition-all shadow-xl shadow-[#D32F2F]/20" data-id="element-1931">
                Request Assessment
              </button>
              <button onClick={() => setActivePage('about')} className="px-8 py-4 bg-white/10 border border-white/20 rounded-xl font-bold hover:bg-white/20 transition-all" data-id="element-1932">
                View Capabilities
              </button>
            </div>
          </motion.div>
          <div className="hidden lg:block relative" data-id="element-1933">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 rounded-[32px] p-8 border border-white/10 backdrop-blur-sm" data-id="element-1934">
              <div className="space-y-6" data-id="element-1935">
                <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-1936">
                  <div data-id="element-1937">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-1" data-id="element-1938">
                      Risk Exposure
                    </p>
                    <p className="text-2xl font-black text-white" data-id="element-1939">
                      Low / Managed
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center text-[#FFD700]" data-id="element-1940">
                    <Shield size={24} data-id="element-1941" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 pt-2" data-id="element-1942">
                  <div className="p-4 bg-[#FFD700]/10 rounded-xl border border-[#FFD700]/20 text-center" data-id="element-1943">
                    <Activity size={20} className="text-[#FFD700] mx-auto mb-2" data-id="element-1944" />
                    <p className="text-[10px] font-bold text-white/60 uppercase" data-id="element-1945">
                      Active Policies
                    </p>
                    <p className="text-lg font-black text-white" data-id="element-1946">4</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10 text-center" data-id="element-1947">
                    <TrendingUp size={20} className="text-white/60 mx-auto mb-2" data-id="element-1948" />
                    <p className="text-[10px] font-bold text-white/60 uppercase" data-id="element-1949">
                      YTD Savings
                    </p>
                    <p className="text-lg font-black text-white" data-id="element-1950">18%</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10 text-center" data-id="element-1951">
                    <AlertTriangle size={20} className="text-white/60 mx-auto mb-2" data-id="element-1952" />
                    <p className="text-[10px] font-bold text-white/60 uppercase" data-id="element-1953">
                      Open Claims
                    </p>
                    <p className="text-lg font-black text-white" data-id="element-1954">0</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#001a4d] -skew-x-12 translate-x-1/4" data-id="element-1955"></div>
      </section>

      {/* 3-Column Grid */}
      <section className="py-24 px-8 max-w-7xl mx-auto" data-id="element-1956">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1957">
          {[{
          title: 'General Liability',
          icon: Shield,
          desc: 'Comprehensive protection against third-party claims and operational risks.'
        }, {
          title: 'Commercial Property',
          icon: Building2,
          desc: 'Safeguarding your physical assets, inventory, and business locations.'
        }, {
          title: 'Workers Comp',
          icon: Users,
          desc: 'Statutory coverage designed to protect your most valuable asset: your people.'
        }].map((item, i) => <div key={i} className="p-10 bg-[#F3F6F9] rounded-[16px] border border-slate-200 hover:border-[#002366] transition-all group" data-id="element-1958">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-[#002366] mb-8 shadow-sm group-hover:bg-[#002366] group-hover:text-white transition-all" data-id="element-1959">
                <item.icon size={28} data-id="element-1960" />
              </div>
              <h3 className="text-2xl font-black mb-4" data-id="element-1961">{item.title}</h3>
              <p className="text-slate-500 font-medium leading-relaxed mb-6" data-id="element-1962">
                {item.desc}
              </p>
              <button onClick={() => setActivePage('quote')} className="text-sm font-black uppercase tracking-widest text-[#002366] flex items-center gap-2" data-id="element-1963">
                Learn More <ArrowRight size={16} data-id="element-1964" />
              </button>
            </div>)}
        </motion.div>
      </section>

      {/* Lead Magnet */}
      <section className="py-24 px-8" data-id="element-1965">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-5xl mx-auto bg-[#002366] rounded-[32px] p-16 text-white flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1966">
          <div className="max-w-md" data-id="element-1967">
            <h2 className="text-3xl font-black mb-4" data-id="element-1968">
              Operational Resilience: A Case Study
            </h2>
            <p className="text-white/60 font-medium mb-8" data-id="element-1969">
              How we saved a mid-market logistics firm 24% on premiums while
              increasing coverage limits.
            </p>
            <button onClick={() => setActivePage('quote')} className="flex items-center gap-3 px-6 py-3 bg-[#FFD700] text-[#002366] rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all" data-id="element-1970">
              <FileText size={18} data-id="element-1971" /> Download Case Study
            </button>
          </div>
          <div className="w-full md:w-64 aspect-[3/4] bg-white/10 rounded-2xl border border-white/20 flex items-center justify-center" data-id="element-1972">
            <FileText size={64} className="text-white/20" data-id="element-1973" />
          </div>
        </motion.div>
      </section>

      {/* Enterprise CTA Form */}
      <section className="py-24 px-8 bg-slate-50" data-id="element-1974">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-4xl mx-auto bg-white rounded-[40px] border border-slate-200 shadow-2xl p-12" data-id="element-1975">
          <div className="text-center mb-12" data-id="element-1976">
            <h2 className="text-4xl font-black text-[#002366] mb-4 tracking-tight" data-id="element-1977">
              Request Enterprise Consultation
            </h2>
            <p className="text-slate-500 font-medium" data-id="element-1978">
              Schedule a strategic risk assessment with our senior architects.
            </p>
          </div>
          <form onSubmit={e => {
          e.preventDefault();
          setActivePage('quote');
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1979">
            <div className="space-y-2" data-id="element-1980">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1981">
                Company Name
              </label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" placeholder="Global Logistics Inc." data-id="element-1982" />
            </div>
            <div className="space-y-2" data-id="element-1983">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1984">
                Industry
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" data-id="element-1985">
                <option data-id="element-1986">Transportation & Logistics</option>
                <option data-id="element-1987">Real Estate Portfolio</option>
                <option data-id="element-1988">Manufacturing</option>
                <option data-id="element-1989">Technology</option>
              </select>
            </div>
            <div className="space-y-2" data-id="element-1990">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1991">
                Work Email
              </label>
              <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" placeholder="exec@company.com" data-id="element-1992" />
            </div>
            <div className="space-y-2" data-id="element-1993">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1994">
                Request Type
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" data-id="element-1995">
                <option data-id="element-1996">Book a Demo</option>
                <option data-id="element-1997">Request Callback</option>
                <option data-id="element-1998">Custom Risk Audit</option>
              </select>
            </div>
            <button type="submit" className="md:col-span-2 py-5 bg-[#DC143C] text-white rounded-xl font-black text-lg shadow-xl shadow-[#DC143C]/20 hover:bg-[#B21031] transition-all" data-id="element-1999">
              Submit Request
            </button>
          </form>
        </motion.div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-8 bg-slate-50 border-t border-slate-200" data-id="element-2000">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="max-w-7xl mx-auto text-center" data-id="element-2001">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 mb-12" data-id="element-2002">
            Institutional Partners
          </p>
          <div className="flex flex-wrap justify-center gap-16 text-slate-300 font-black text-xl md:text-2xl uppercase tracking-[0.2em]" data-id="element-2003">
            <span data-id="element-2004">Travelers</span>
            <span data-id="element-2005">Chubb</span>
            <span data-id="element-2006">Hartford</span>
            <span data-id="element-2007">CNA</span>
          </div>
        </motion.div>
      </section>

      <SimonsFooter data-id="element-2008" />
    </div>;
}